import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Grid } from 'ui-toolkit';
import { AdvancedOrdersSearchForm } from './AdvancedOrdersSearchForm';
import { Form, Formik } from 'formik';
import { ICountiesData } from '../../store/counties';
import { IAdminUserData } from '../../types/administration/userAdministration/IAdminUser';
import { ILenderCompanyData } from '../../types/lenders/ILenderCompany';
import { ITagData } from '../../types/tags/ITagData';
import { eUSAState } from '../../ui-toolkit/enums/states';
import { IClientData } from '../../ui-toolkit/orders/interfaces/IClient';
import { DefaultOrdersSearchForm } from '../../ui-toolkit/orders/searchOrder/components/DefaultOrdersSearchForm';
import { searchOrdersSpecification } from '../../ui-toolkit/orders/searchOrder/validation/searchOrdersSpecification';
import { ISearchOrdersSpecificationData } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrdersSpecification';

const notAdvancesSearchFields = [
  'pageSize',
  'pageNumber',
  'sortBy',
  'fileNumbers',
  'partyName',
  'streetAddress',
  'unit',
];

const getInitialAdvancedSearchValue = (query: ISearchOrdersSpecificationData): boolean => {
  return Object.keys(query).some((key) => !notAdvancesSearchFields.includes(key));
};

const SearchButtonsDiv = styled.div`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
`;

interface IOrdersSearchFormProps {
  adminUsers: IAdminUserData[];
  clients: IClientData[];
  lenders: ILenderCompanyData[];
  onSearchOrder: (value: ISearchOrdersSpecificationData) => void;
  initialValue: ISearchOrdersSpecificationData;
  counties: ICountiesData;
  tags: ITagData[];
  onStateSelect: (state: eUSAState | null) => void;
  onReset: () => void;
}

export const OrdersSearchForm: React.FC<IOrdersSearchFormProps> = ({
  adminUsers,
  lenders,
  clients,
  initialValue,
  onSearchOrder,
  counties,
  tags,
  onStateSelect,
  onReset,
}) => {
  const clientsOptions = useMemo(
    () =>
      clients.map((client) => ({
        label: client.name,
        value: client.id,
      })),
    [clients]
  );
  const lendersOptions = useMemo(
    () =>
      lenders.map((lenders) => ({
        label: lenders.name,
        value: lenders.id,
      })),
    [lenders]
  );
  const adminsOptions = useMemo(
    () =>
      adminUsers.map((admin) => ({
        label: admin.fullName,
        value: admin.id,
      })),
    [adminUsers]
  );

  const [advancedSearch, setAdvancedSearch] = useState(getInitialAdvancedSearchValue(initialValue));

  useEffect(() => {
    setAdvancedSearch(getInitialAdvancedSearchValue(initialValue));
  }, [initialValue]);

  return (
    <Formik
      initialValues={initialValue}
      validateOnMount={true}
      validationSchema={searchOrdersSpecification(lendersOptions, clientsOptions)}
      onSubmit={onSearchOrder}
      onReset={onReset}
    >
      {({ values, handleSubmit, isValid }) => (
        <Form onSubmit={handleSubmit}>
          <Grid templateColumns={'1fr max-content'} alignItems={'start'}>
            <div>
              {advancedSearch ? (
                <AdvancedOrdersSearchForm
                  admins={adminsOptions}
                  lenders={lendersOptions}
                  clients={clientsOptions}
                  tags={tags}
                  onStateSelect={onStateSelect}
                  counties={values.state ? counties[values.state] : []}
                />
              ) : (
                <DefaultOrdersSearchForm />
              )}
              <Button intent={'ghost'} isInline type={'button'} onClick={() => setAdvancedSearch(!advancedSearch)}>
                {advancedSearch ? 'Basic Search' : 'Advanced Search'}
              </Button>
            </div>
            <SearchButtonsDiv>
              <Button intent={'primary'} type={'submit'} disabled={!isValid}>
                Search
              </Button>
              <Button intent={'ghost'} isInline type={'reset'}>
                Clear Search
              </Button>
            </SearchButtonsDiv>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
